import { isEqual } from "lodash";
import { createContext, Dispatch, useContext, useEffect, useReducer } from "react";
import { ArsTableRow } from "../table";
import { arsListingReducer } from "./arsListingReducer";
import { buildScaffoldFromCalculatedRows, datasetsFromColumns } from "./functions";
import {
  ARSL_CHANGE_SENT,
  ARSL_LOAD_DATA,
  ARSL_SET_TLF_VIEW_MODE,
  ARSL_UPDATE_SCAFFOLD,
  ArsListingAction,
  ArsListingBody,
  ArsListingSettings,
  ArsListingState,
} from "./interface";

interface ArsListingContextProps<T> {
  state: ArsListingState<T>;
  dispatch: Dispatch<ArsListingAction<T>>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ArsListingContext = createContext<ArsListingContextProps<any> | null>(null);

interface ArsListingContextProviderProps<T> {
  children: React.ReactNode;
  bodyData: ArsListingBody<T>;
  calculatedTableRows: ArsTableRow<T>[];
  settings: ArsListingSettings<T>;
  updateBodyData: (ret: ArsListingBody<T>) => void;
}

/**
 * Provider that supplies listing state and dispatch to child components.
 *
 * @param props - Component props
 * @param props.children - Child nodes wrapped by the provider
 * @param props.bodyData - Body data to render
 * @param props.calculatedTableRows - Calculated table rows to render
 * @param props.settings - Options for listing types
 * @param props.updateBodyData - Function to partially update the body data
 * @returns ArsListingContextProvider component
 */
export const ArsListingContextProvider = <T,>({
  children,
  bodyData,
  calculatedTableRows,
  settings,
  updateBodyData,
}: ArsListingContextProviderProps<T>) => {
  const [state, dispatch] = useReducer(arsListingReducer, {
    bodyData,
    calculatedTableRows,
    scaffoldModel: buildScaffoldFromCalculatedRows(calculatedTableRows, settings.editorProps),
    changed: false,
    datasets: datasetsFromColumns(bodyData.arsListingColumns),
    settings,
  });

  useEffect(() => {
    const eq = isEqual(state.bodyData, bodyData);
    // Send changes up
    if (!eq && state.bodyData.id === bodyData.id && state.changed) {
      updateBodyData(state.bodyData);
      dispatch({ operation: ARSL_CHANGE_SENT });
    }
    // Accept new input if it differs from current state and wasn't just sent
    else if (state.bodyData.id !== bodyData.id || !eq) {
      dispatch({ operation: ARSL_LOAD_DATA, bodyData });
    }
  }, [bodyData, state.changed, state.bodyData, updateBodyData]);

  useEffect(() => {
    if (!isEqual(state.settings.tlfViewMode, settings.tlfViewMode)) {
      dispatch({ operation: ARSL_SET_TLF_VIEW_MODE, tlfViewMode: settings.tlfViewMode });
    }
  }, [settings.tlfViewMode, state.settings.tlfViewMode]);

  useEffect(() => {
    const eq = isEqual(state.calculatedTableRows, calculatedTableRows ?? []);
    if (!eq && calculatedTableRows) {
      dispatch({ operation: ARSL_UPDATE_SCAFFOLD, calculatedTableRows });
    }
  }, [calculatedTableRows, state.calculatedTableRows]);

  return (
    <ArsListingContext.Provider value={{ state, dispatch }}>{children}</ArsListingContext.Provider>
  );
};

ArsListingContextProvider.displayName = "ArsListingContextProvider";

/**
 * Custom hook to access the ArsListing context.
 *
 * @returns The listing context object
 */
export const useArsListing = <T,>() => {
  const context = useContext(ArsListingContext);
  return !context
    ? {
        state: null,
        dispatch: () => console.error("WARNING: Dispatch not initiated"),
      }
    : (context as ArsListingContextProps<T>);
};
